"use client";
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { apiSubscribeNewsletter } from '@/apis/page_optimization';

interface Props {
    companyId: string;
}

const Newsletter = ({ companyId }: Props) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [focused, setFocused] = useState(false);

    const validateEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) {
            toast.error('Please enter your email address.');
            return;
        }
        if (!validateEmail(email)) {
            toast.error('Please enter a valid email address.');
            return;
        }

        setLoading(true);
        try {
            const response = await apiSubscribeNewsletter(companyId, email);
            if (response.message === 'Subscribed successfully') {
                toast.success('Welcome to our exclusive deals community!', { autoClose: 3000 });
                setEmail('');
            } else {
                toast.error('You have already subscribed to our newsletter.');
            }
        } catch {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    return (
        <section
            className="relative w-full py-16 md:py-20 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-850 to-gray-900"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
            {/* Premium background effects */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {/* Sophisticated gradient overlay */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(74,154,110,0.08)_0%,transparent_50%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(74,154,110,0.05)_0%,transparent_60%)]" />

                {/* Refined grid pattern */}
                <div
                    className="absolute inset-0 opacity-[0.015]"
                    style={{
                        backgroundImage: `linear-gradient(rgba(74,154,110,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(74,154,110,0.3) 1px, transparent 1px)`,
                        backgroundSize: '50px 50px',
                    }}
                />

                {/* Subtle floating elements */}
                <div className="absolute top-16 left-1/4 w-2 h-2 bg-[#5D27A1]/20 rounded-full blur-sm animate-pulse" />
                <div className="absolute top-1/2 right-1/3 w-1.5 h-1.5 bg-[#5D27A1]/15 rounded-full blur-sm animate-pulse" style={{ animationDelay: '2s' }} />
                <div className="absolute bottom-20 left-1/3 w-1 h-1 bg-[#5D27A1]/25 rounded-full blur-sm animate-pulse" style={{ animationDelay: '4s' }} />
            </div>

            {/* Elegant top border */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#5D27A1]/40 to-transparent" />

            <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="max-w-7xl mx-auto">
                    {/* Premium newsletter container */}
                    <div className="relative">
                        {/* Sophisticated glow effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-[#5D27A1]/8 via-[#5D27A1]/4 to-[#5D27A1]/8 rounded-3xl blur-2xl scale-105" />

                        {/* Main premium container */}
                        <div className="relative bg-gradient-to-r from-white/[0.03] via-white/[0.07] to-white/[0.03] backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl shadow-black/20">
                            <div className="grid lg:grid-cols-12 gap-0 items-center min-h-[200px]">

                                {/* Left - Premium header section */}
                                <div className="lg:col-span-5 p-8 md:p-10">
                                    <div className="flex items-start gap-4 mb-6">
                                        <div className="relative flex-shrink-0">
                                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#5D27A1]/20 via-[#5D27A1]/10 to-transparent border border-[#5D27A1]/30 flex items-center justify-center backdrop-blur-sm">
                                                <svg className="w-7 h-7 text-[#5D27A1]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                                                </svg>
                                            </div>
                                            <div className="absolute inset-0 w-14 h-14 rounded-2xl border border-[#5D27A1]/10 animate-pulse" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight mb-2">
                                                Exclusive <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5D27A1] to-[#471d7c]">Premium</span> Deals
                                            </h3>
                                            <div className="flex items-center gap-3">
                                                <div className="h-[2px] w-12 bg-gradient-to-r from-[#5D27A1] to-[#5D27A1]/50 rounded-full" />
                                                <div className="h-[2px] w-6 bg-gradient-to-r from-[#5D27A1]/50 to-transparent rounded-full" />
                                            </div>
                                        </div>
                                    </div>

                                    <p className="text-gray-300 text-base leading-relaxed mb-6">
                                        Join our exclusive community and receive hand-picked deals, insider discounts, and early access to sales.
                                        <span className="text-[#5D27A1] font-semibold">Over 50,000</span> subscribers already saving.
                                    </p>

                                    {/* Trust indicators */}
                                    <div className="flex items-center gap-6 text-sm text-gray-400">
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center">
                                                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <span>100% Secure</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                                                <svg className="w-3 h-3 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <span>Privacy Protected</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Center - Premium features */}
                                <div className="hidden lg:block lg:col-span-3 px-8 py-10 border-l border-r border-white/[0.08] bg-gradient-to-b from-white/[0.02] to-transparent">
                                    <div className="space-y-6">
                                        {[
                                            {
                                                icon: (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                                                    </svg>
                                                ),
                                                title: 'Flash Alerts',
                                                desc: 'Instant notifications'
                                            },
                                            {
                                                icon: (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                                                    </svg>
                                                ),
                                                title: 'Curated Selection',
                                                desc: 'Hand-picked offers'
                                            },
                                            {
                                                icon: (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                ),
                                                title: 'Maximum Savings',
                                                desc: 'Up to 80% discounts'
                                            }
                                        ].map((feature, index) => (
                                            <div key={index} className="group flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5D27A1]/15 to-[#5D27A1]/5 border border-[#5D27A1]/20 flex items-center justify-center text-[#5D27A1] group-hover:border-[#5D27A1]/40 group-hover:bg-[#5D27A1]/20 group-hover:scale-105 transition-all duration-300">
                                                    {feature.icon}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-white mb-1 group-hover:text-[#5D27A1] transition-colors">
                                                        {feature.title}
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        {feature.desc}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* Right - Premium form */}
                                <div className="lg:col-span-4 p-8 md:p-10">
                                    <form onSubmit={handleSubscribe} className="space-y-6">
                                        <div className="relative">
                                            {/* Premium input container */}
                                            <div className={`relative transition-all duration-500 ${focused ? 'transform scale-[1.02]' : ''}`}>
                                                {/* Animated glow border */}
                                                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r from-[#5D27A1] via-[#471d7c] to-[#5D27A1] transition-all duration-300 ${focused || email ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ padding: '2px' }}>
                                                    <div className="h-full w-full rounded-2xl bg-gray-900" />
                                                </div>

                                                <div className={`relative flex items-center bg-white/[0.05] backdrop-blur-sm border rounded-2xl overflow-hidden transition-all duration-300 ${focused ? 'border-[#5D27A1]/50 shadow-lg shadow-[#5D27A1]/20' : 'border-white/10 hover:border-white/20'}`}>
                                                    <input
                                                        type="email"
                                                        placeholder="Enter your email address"
                                                        className="flex-1 bg-transparent border-none outline-none px-6 py-4 text-white placeholder-gray-400 text-base font-medium"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        onFocus={() => setFocused(true)}
                                                        onBlur={() => setFocused(false)}
                                                        disabled={loading}
                                                    />

                                                    {/* Premium validation indicator */}
                                                    {email && (
                                                        <div className="px-4">
                                                            {validateEmail(email) ? (
                                                                <div className="w-6 h-6 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center backdrop-blur-sm">
                                                                    <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                </div>
                                                            ) : (
                                                                <div className="w-6 h-6 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center backdrop-blur-sm">
                                                                    <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading || !validateEmail(email)}
                                            className="group relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-[#5D27A1] via-[#471d7c] to-[#5D27A1] py-4 px-8 text-white font-semibold text-base transition-all duration-500 hover:shadow-2xl hover:shadow-[#5D27A1]/30 hover:-translate-y-1 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
                                        >
                                            {/* Premium button effects */}
                                            <div className="absolute inset-0 bg-gradient-to-r from-[#471d7c] via-[#5D27A1] to-[#471d7c] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                            {/* Sophisticated shimmer */}
                                            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" />

                                            <span className="relative z-10 flex items-center justify-center gap-3">
                                                {loading ? (
                                                    <>
                                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                        <span>Processing...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>Subscribe to Premium Deals</span>
                                                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                                                        </svg>
                                                    </>
                                                )}
                                            </span>
                                        </button>

                                        {/* Premium disclaimer */}
                                        <p className="text-center text-xs text-gray-100 leading-relaxed">
                                            By subscribing, you agree to receive promotional emails. Unsubscribe anytime.
                                            <br />Your privacy is protected and data is never shared.
                                        </p>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Elegant bottom border */}
            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#5D27A1]/40 to-transparent" />
        </section>
    );
};

export default Newsletter;